import type { Context } from '../types/context';
import { sanitizeFilterLabel } from '../utils';
import { getSelectedFilter } from '.';

/**
 * Custom error for missing filter tree context
 */
class FilterTreeMissingError extends Error {
  constructor(
    message: string = "FilterTree is missing from context – ensure it's provided upstream."
  ) {
    super(message);
    this.name = 'FilterTreeMissingError';
  }
}

const TOGGLE_SELECTOR_SUFFIX = '-toggle' as const;

// Move up for logical flow
function escapeCssSelector(selector: string): string {
  // Escapes characters with special meaning in CSS selectors.
  // See: https://developer.mozilla.org/en-US/docs/Web/API/CSS/escape
  return CSS.escape(selector);
}

export function getToggleSelector(id: string, suffix = TOGGLE_SELECTOR_SUFFIX): string {
  const escapedId = escapeCssSelector(id);
  return `#${escapedId}${suffix}`;
}

interface FilterOption {
  filterOptionId: string;
  label: string;
}

interface FilterTree {
  filterId: string;
  options: FilterOption[];
}

interface SelectedFilter {
  data?: {
    key: string;
    label: string;
  };
}

interface Translations {
  filterOptions?: Record<string, string>;
}

type ContextWithFilterTree = Context & {
  filterTree: FilterTree;
};

/**
 * Replace or insert the visible text content of an HTMLElement
 * without modifying its child elements such as <div>, <svg>, or others.
 *
 * @param el - The target HTMLElement to modify.
 * @param newText - The new text content to set.
 *
 * Behavior:
 * - Finds the first non-empty text node among direct children and replaces its content.
 * - If no text node is found, inserts a new text node before the first element child.
 */
function setElementText(el: HTMLElement | null, newText: string): void {
  if (!el) return;

  const textNode = Array.from(el.childNodes).find(
    (n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim() !== ''
  );

  if (textNode) {
    textNode.textContent = newText;
  } else {
    // Also sanitize aria-label attribute to prevent XSS
    if (el.hasAttribute('aria-label')) {
      el.setAttribute('aria-label', `Filter by: ${newText}`);
    }
    el.insertBefore(document.createTextNode(newText), el.firstChild);
  }
}

// Type guard for safety (replaces assertion)
function hasFilterTree(context: Context): context is ContextWithFilterTree {
  return 'filterTree' in context && context.filterTree !== undefined && context.filterTree !== null;
}

/**
 * Safely extracts filterTree from context, throwing if absent.
 * @param context - The app context.
 * @returns The filterTree.
 * @throws Error if filterTree is missing.
 */
export function getFilterTree(context: Context): FilterTree {
  if (!hasFilterTree(context)) {
    throw new FilterTreeMissingError();
  }
  return context.filterTree;
}

// Helper to build translation key (makes parsing less brittle)
function buildTranslationKey(filterId: string, optionKey: string): string {
  return `filterOption|${filterId}|${optionKey}`;
}
export function getTranslatedFilterOption(
  context: Context,
  key: string,
  defaultValue?: string
): string {
  const filterTree = getFilterTree(context);
  const filterId = filterTree.filterId;

  if (!filterId || !key) {
    return defaultValue ?? key;
  }

  const translations = context.app.translation as Translations;
  const filterOptions = translations.filterOptions ?? {};

  if (filterOptions === null || typeof filterOptions !== 'object') {
    return defaultValue ?? key;
  }

  // Use built key for exact match (less error-prone than find + split)
  const translationKey = buildTranslationKey(filterId, key);
  if (translationKey in filterOptions && typeof filterOptions[translationKey] === 'string') {
    return filterOptions[translationKey];
  }

  return defaultValue ?? key;
}

/**
 * Applies translations to filter option labels in the DOM.
 * @param context - The app context.
 */
export function applyTranslationsToFilterOptions(context: Context): void {
  const filterTree = getFilterTree(context);
  if (!Array.isArray(filterTree.options)) {
    return;
  }

  for (const option of filterTree.options) {
    if (typeof option.filterOptionId !== 'string' || !option.label) {
      // Guard missing label
      continue;
    }
    const { filterOptionId, label: defaultLabel } = option;
    // @ts-expect-error - context.app.xss exists at runtime
    const isXssDisabled = context.app?.xss?.filter?.disabled;
    const translatedLabel = getTranslatedFilterOption(context, filterOptionId, defaultLabel);

    // Sanitize the final label (covers both translation and default fallback)
    const finalLabel = isXssDisabled ? translatedLabel : sanitizeFilterLabel(translatedLabel);

    const elements = document.querySelectorAll(getToggleSelector(filterOptionId));
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        // Narrow type for safety
        setElementText(element, finalLabel);
      }
    });
  }

  const selectedFilters = getSelectedFilter(context);
  if (Array.isArray(selectedFilters) && selectedFilters.length > 0) {
    applyTranslationsToRefineBy(context, selectedFilters);
  }
}

/**
 * Updates labels in selected filters (mutates in place; consider immutable variant if needed).
 * @param context - The app context.
 * @param selectedFilters - Array of filters to update.
 */
export function applyTranslationsToRefineBy(
  context: Context,
  selectedFilters: SelectedFilter[]
): void {
  if (!Array.isArray(selectedFilters)) {
    return;
  }

  for (const selectedFilter of selectedFilters) {
    if (selectedFilter.data?.key && typeof selectedFilter.data.label === 'string') {
      selectedFilter.data.label = getTranslatedFilterOption(
        context,
        selectedFilter.data.key,
        selectedFilter.data.label
      );
    }
  }
}
